// SALDP white-labelling. Added by Sullivan Digital, 2026-10-26. Licensed under the GNU AGPL v3; see LICENSE-AGPL.txt.
/* eslint-disable metabase/no-literal-metabase-strings -- this module finds and replaces the literal name */

/**
 * Replaces the word "Metabase" in user-visible translated strings with the
 * brand name, at lookup time and for every locale, so no `.po` file has to be
 * edited. Legal attributions ("Metabase, Inc.", the trademark notice) and other
 * words ("Metabot", "metabase.com") are left alone.
 */

const BRAND_WORD = /\bMetabase\b(?!, Inc)(?!\s+is\s+a\s+[Tt]rademark)/g;
const KEEP_PHRASES = [/Trademark of Metabase/i, /Based on Metabase/i];

export function replaceBrandName(text: string, brandName: string): string {
  if (!brandName || !text.includes("Metabase")) {
    return text;
  }
  if (KEEP_PHRASES.some((phrase) => phrase.test(text))) {
    return text;
  }
  return text.replace(BRAND_WORD, brandName);
}

type TranslationEntry = { msgid?: string; msgstr?: string[] };
export type TranslationContexts = Record<
  string,
  Record<string, TranslationEntry>
>;

const isEntry = (value: unknown): value is TranslationEntry =>
  typeof value === "object" && value !== null;

function brandContext(
  context: Record<string, TranslationEntry>,
  getBrandName: () => string,
): Record<string, TranslationEntry> {
  const cache = new Map<string, TranslationEntry>();

  return new Proxy(context, {
    get(target, key, receiver) {
      const entry: unknown = Reflect.get(target, key, receiver);
      if (typeof key !== "string") {
        return entry;
      }
      const brandName = getBrandName();
      const cacheKey = `${brandName}\u0000${key}`;
      const cached = cache.get(cacheKey);
      if (cached) {
        return cached;
      }

      let branded: TranslationEntry | undefined;
      if (isEntry(entry) && Array.isArray(entry.msgstr)) {
        if (entry.msgstr.some((form) => form.includes("Metabase"))) {
          branded = {
            ...entry,
            msgstr: entry.msgstr.map((form) =>
              replaceBrandName(form, brandName),
            ),
          };
        }
      } else if (entry === undefined && key.includes("Metabase")) {
        // Untranslated (e.g. English): serve the msgid itself, branded.
        const replaced = replaceBrandName(key, brandName);
        if (replaced !== key) {
          branded = { msgid: key, msgstr: [replaced] };
        }
      }

      if (!branded) {
        return entry;
      }
      cache.set(cacheKey, branded);
      return branded;
    },
  });
}

/**
 * Wraps the contexts of a locale that has already been passed to ttag's
 * `addLocale`. ttag keeps a reference to the locale object and replaces its
 * `translations` with its own normalised copy, which is what is wrapped here;
 * `brand-i18n.unit.spec.ts` fails if a ttag upgrade changes that.
 */
export function brandRegisteredLocale(
  localeData: { translations?: TranslationContexts },
  getBrandName: () => string,
): void {
  const translations = localeData.translations;
  if (!translations) {
    return;
  }
  const branded: TranslationContexts = Object.fromEntries(
    Object.entries(translations).map(([name, context]) => [
      name,
      brandContext(context, getBrandName),
    ]),
  );
  // A context with no entries of its own falls back to "" in ttag, so it must exist.
  branded[""] ??= brandContext({}, getBrandName);
  localeData.translations = branded;
}
